import { z } from 'zod';
import { positiveAmount, uuid } from './posting.js';
/**
 * Die Verträge der Ereignisablage — Konzept 11.1 („Ingest-API (FameSystems →
 * Buchhaltung)"), Stufe 1.
 *
 * **Der Kanal fehlt, die Ablage nicht.** Konzept 11.1 beschreibt sechs
 * POST-Endpunkte; keiner davon existiert hier, weil der Zugang zur Plattform
 * nicht erteilt ist. Was das Konzept über den Kanal hinaus zusagt — *„Event
 * wird roh persistiert (`inbound_event`) […] Fehlerhafte Events […] werden
 * **nie** verworfen"* — hängt am Kanal nicht und entsteht deshalb zuerst.
 * Diese Datei ist die Aussengrenze dieser Ablage: Sie gilt für den heutigen
 * Hand- und Dateiweg genauso wie für den späteren HTTP-Kanal.
 *
 * **Streng heisst hier: über das Belegte streng, über das Unbelegte still.**
 * Konzept 11.1 beschreibt genau **einen** Ereignisrumpf im Feld —
 * `balance-topup` — und für alle Ereignisse denselben Pflichtteil, den
 * Idempotenzschlüssel. Mehr ist nicht belegt. Ein Schema, das die restlichen
 * vier Ereignisse mit plausiblen Feldern ausstattete, wäre eine Erfindung mit
 * Vertragswirkung: Der erste echte Export der Plattform bräche daran, und
 * niemand wüsste, ob die Plattform oder die Annahme falsch liegt. Geprüft wird
 * deshalb ein **schlanker Pflichtkern**; alles Übrige geht **roh** in
 * `payload` und bleibt dort vollständig erhalten.
 *
 * **Und deshalb ist kein Nutzdatenschema `.strict()`.** Ein unbekanntes Feld
 * ist kein Fehler, sondern der Normalfall eines fremden Systems — es
 * abzuweisen hiesse, eine Lieferung zu verwerfen, und genau das schliesst
 * Konzept 11.1 aus. Streng ist die **Hülle** (unten `ingestAnnahmeSchema`):
 * Kanal und Ereignistyp sind geschlossene Wertebereiche, und ein Feld, das die
 * Hülle nicht kennt, ist ein Aufrufsfehler und keine Nutzlast.
 *
 * **Beträge verlassen das Schema als `Money`** (Hausregel 1) — dieselbe
 * `positiveAmount`-Kette wie in jedem anderen Vertrag dieses Pakets.
 */
/**
 * Woher ein Ereignis kam.
 *
 * Diese Liste ist die Quelle der Wahrheit, auch für die Datenbank: Der `CHECK`
 * auf `inbound_event.kanal` in Migration `0101` führt dieselben drei Werte.
 *
 * | Wert | Was ihn schreibt |
 * |---|---|
 * | `datei` | eine von der Plattform exportierte Ereignisdatei (`pnpm ingest:datei`) |
 * | `api` | der spätere HTTP-Kanal aus Konzept 11.1 — heute ohne Schreiber |
 * | `hand` | von einem Menschen einzeln eingeworfen |
 *
 * `api` steht im Vertrag, obwohl ihn heute niemand schreibt. Er steht da, weil
 * der Wertebereich sonst mit dem Kanal bräche — und ein Leser, der heute mit
 * dreien rechnet, morgen an einem Wert scheiterte, den er nicht kennt.
 */
export const INGEST_KANAELE = ['datei', 'api', 'hand'];
/** Ein Kanal der Ereignisablage. */
export const ingestKanalSchema = z.enum(INGEST_KANAELE);
/**
 * Die Ereignisarten — **wörtlich** aus Konzept 11.1 abgelesen.
 *
 * Das Konzept listet dort sechs `POST`-Endpunkte. Fünf davon sind laufende
 * Ereignisse und stehen hier; der sechste (`POST /api/v1/invoices/import`,
 * „Bestandsrechnungen aus dem Altsystem") ist ausdrücklich ein einmaliger
 * Migrationsweg und gehört zu Phase 7, nicht in diese Ablage.
 *
 * Die Schreibweise ist die des Konzepts und nicht eine eingedeutschte: Sie ist
 * zugleich der Pfadbestandteil, den der spätere HTTP-Kanal trägt, und zwei
 * Schreibweisen für dieselbe Sache wären eine Übersetzungstabelle, die jemand
 * pflegen müsste.
 */
export const INGEST_EREIGNIS_TYPEN = [
    'customer-upserted',
    'balance-topup',
    'order-placed',
    'order-cancelled',
    'refund-issued',
];
/** Eine Ereignisart der Ablage. */
export const ingestEreignisTypSchema = z.enum(INGEST_EREIGNIS_TYPEN);
/**
 * Der Idempotenzschlüssel des Absenders — das **einzige** Feld, das Konzept
 * 11.1 für *alle* Endpunkte zur Pflicht macht („`Idempotency-Key`-Header
 * **Pflicht**", im Rumpfbeispiel als `idempotencyKey`).
 *
 * Er ist zugleich `inbound_event.external_id` und damit die eine Hälfte des
 * Dublettenschutzes. Der Dienst leitet ihn **nicht** ab und erfindet ihn
 * nicht: Ein zweiter, selbst gebildeter Schlüssel wäre eine zweite Wahrheit
 * über dieselbe Frage.
 */
export const idempotenzSchluesselSchema = z
    .string()
    .trim()
    .min(1, 'idempotencyKey fehlt')
    // 200 Zeichen sind eine **Schutzgrenze dieses Repositories** und keine
    // Angabe des Konzepts — es nennt keine. Sie steht da, weil der Schlüssel in
    // einen B-Tree-Index geht (`inbound_event_dublette`) und PostgreSQL dort bei
    // rund 2700 Byte je Eintrag mit `54000` abbricht: ohne Grenze käme ein
    // absurd langer Schlüssel als 500 zurück statt als Eingabefehler. Das
    // Konzeptbeispiel ist 22 Zeichen lang.
    .max(200, 'idempotencyKey ist zu lang');
/** Der Pflichtkern, den jedes Ereignis trägt. Offen für alles Weitere — siehe Kopf. */
export const ingestPflichtkernSchema = z
    .object({ idempotencyKey: idempotenzSchluesselSchema })
    .passthrough();
/**
 * Die Guthabenaufladung — das **einzige** Ereignis, dessen Rumpf Konzept 11.1
 * im Feld zeigt. Die sieben Felder unten stehen dort wörtlich; keines ist
 * hinzugedacht.
 *
 * `paymentMethod` bleibt eine nichtleere Zeichenkette und **kein**
 * Aufzählungstyp: Das Konzept zeigt genau einen Wert (`"paypal"`) und nennt
 * die Menge nirgends. Eine aus drei Zahlwegen geratene Aufzählung wiese den
 * ersten Export mit einem vierten Wert ab.
 */
export const ingestGuthabenAufladungSchema = ingestPflichtkernSchema.extend({
    externalCustomerId: z.string().trim().min(1, 'externalCustomerId fehlt'),
    amount: positiveAmount,
    currency: z.string().regex(/^[A-Z]{3}$/, 'Währung als dreistelliger ISO-Code erwartet'),
    paymentMethod: z.string().trim().min(1, 'paymentMethod fehlt'),
    providerTransactionId: z.string().trim().min(1, 'providerTransactionId fehlt'),
    receivedAt: z.string().datetime({ offset: true, message: 'receivedAt als ISO-8601-Zeitpunkt erwartet' }),
});
/**
 * Das Prüfschema je Ereignisart.
 *
 * Vier der fünf Arten stehen bewusst auf dem blossen Pflichtkern. Das ist die
 * ehrliche Abbildung des Belegten und keine Lücke, die jemand vergessen hat —
 * siehe den Kopf dieser Datei. Sobald ein echter Export der Plattform vorliegt
 * und gemessen ist, wächst hier ein Feld nach, und die Ablage darunter ändert
 * sich dafür nicht.
 *
 * **Eine Funktion und keine Tabelle `Record<Typ, ZodType>`.** Die beiden
 * Schemas haben verschiedene Ergebnistypen (`amount` verlässt das eine als
 * `Money`), und `z.ZodType` ist in seinem Ergebnistyp nicht kovariant — eine
 * gemeinsame Tabelle liesse sich nur über eine Typbehauptung schreiben, und
 * eine Typbehauptung ist genau die Stelle, an der ein Vertrag still falsch
 * wird. Die Prüfung findet deshalb hier statt und gibt nur ihr Ergebnis
 * heraus.
 */
export function pruefeIngestPayload(typ, payload) {
    const ergebnis = typ === 'balance-topup'
        ? ingestGuthabenAufladungSchema.safeParse(payload)
        : ingestPflichtkernSchema.safeParse(payload);
    return ergebnis.success ? { ok: true } : { ok: false, fehler: ergebnis.error };
}
/**
 * Der Idempotenzschlüssel eines Rumpfes, oder `undefined`.
 *
 * Er ist zugleich `inbound_event.external_id`. Ausgelesen wird er hier und
 * nicht aus dem Prüfergebnis, weil das Prüfergebnis den Rumpf **kanonisiert**
 * zurückgäbe (`amount` als `Money`) — abgelegt wird aber, was ankam.
 */
export function ingestIdempotenzSchluessel(payload) {
    if (typeof payload !== 'object' || payload === null)
        return undefined;
    const roh = payload.idempotencyKey;
    const geprueft = idempotenzSchluesselSchema.safeParse(roh);
    return geprueft.success ? geprueft.data : undefined;
}
/**
 * Was der Dienst zur Annahme entgegennimmt — die **strenge Hülle**.
 *
 * `.strict()` und nicht `.passthrough()`, anders als bei den Nutzdaten: Was
 * hier steht, schreibt kein fremdes System, sondern ein Aufrufer dieses
 * Repositories. Ein unbekanntes Feld ist dort ein Tippfehler und keine
 * Lieferung, und ein Tippfehler soll auffallen.
 *
 * `payload` bleibt roh. Die Prüfung des Rumpfes läuft über
 * `pruefeIngestPayload()` und **verändert das Abgelegte nicht** — abgelegt
 * wird, was ankam.
 */
export const ingestAnnahmeSchema = z
    .object({
    kanal: ingestKanalSchema,
    ereignisTyp: ingestEreignisTypSchema,
    payload: z.record(z.unknown()),
})
    .strict();
/** Eine Zeile der Ereignisablage, wie ein Leser sie sieht. */
export const ingestEreignisZeileSchema = z.object({
    id: uuid,
    kanal: ingestKanalSchema,
    ereignisTyp: ingestEreignisTypSchema,
    externalId: z.string(),
    empfangenAm: z.string(),
    payloadSha256: z.string(),
    /**
     * `null`, solange die Zeile nicht verarbeitet ist. Sie ist zugleich das
     * Siegel: Nach dem Setzen ist die Zeile abgeschlossen (Migration `0098`).
     */
    verarbeitetAm: z.string().nullable(),
    /**
     * Der Befund der Verarbeitung — der gelungene ebenso wie der gescheiterte
     * Versuch. Ein Befund **ohne** `verarbeitetAm` ist die Dead-Letter-Spur aus
     * Konzept 11.1: Der Versuch ist gescheitert, das Ereignis bleibt offen.
     */
    verarbeitungsBefund: z.record(z.unknown()).nullable(),
});
/**
 * Die Antwort der Ereignisliste.
 *
 * `gesamt` steht neben den Zeilen und wird über dieselben Filter, aber **ohne**
 * die Kappung gezählt — dieselbe Bauform wie die Nachschau der automatischen
 * Kontierungen. Eine Liste, die bei 200 abschneidet und die Gesamtzahl
 * verschweigt, sähe nach einem ruhigen Monat aus, auch wenn tausend Ereignisse
 * eingegangen sind.
 */
export const ingestEreignisListeSchema = z.object({
    ereignisse: z.array(ingestEreignisZeileSchema),
    gesamt: z.number().int().nonnegative(),
    /** Wie viele davon insgesamt noch auf ihre Verarbeitung warten — ungefiltert. */
    offen: z.number().int().nonnegative(),
});
//# sourceMappingURL=ingest.js.map